// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a stop's moves on the live globe (docs/AYNI_XR_STUDIO_MOTION_PLAN.md):
 * the arrival and the hold of `services/studio/film.ts`, played frame by frame
 * with the camera jumped to each sampled view, so the Khipu player, the
 * Studio's preview and the rendered video move alike.
 *
 * Someone who asked for less motion (`prefers-reduced-motion`) gets the stop
 * at once, without orbit or push, and its effects shown still.
 */

import { arrivalView, holdView, resolveTransition, type FilmStop, type FilmTransition, type FilmView } from '../services/studio/film'
import type { KhipuEffects, KhipuTransition } from '../services/khipu'
import type { EffectsClock } from '../services/studio/effects'

export interface LiveMotionHost {
  /** The camera now, or null. */
  view(): FilmView | null
  jumpTo(view: FilmView): void
  reducedMotion(): boolean
  /** Each frame of the hold with its effects (absent: none to paint). */
  paint?(effects: KhipuEffects, clock: EffectsClock): void
  clear?(): void
  /** The animation clock (tests pass their own). */
  now?(): number
  frame?(cb: (t: number) => void): number
  cancelFrame?(id: number): void
}

export interface LiveMotion {
  /** Stop the moves (the camera stays where it is). */
  cancel(): void
  /** Resolves when the arrival has ended (the hold goes on until `cancel` or its end). */
  arrived: Promise<void>
  /** Resolves when the hold has ended, or at once when cancelled. */
  done: Promise<void>
}

/** What a live step needs: where it goes, how, how long it holds, what it shows. */
export interface LiveStop {
  view: FilmView
  transition?: KhipuTransition
  hold: number
  effects?: KhipuEffects
  /** A step that waits for "Next": its effects stay once shown (no fading out). */
  open?: boolean
}

export function playStopLive(host: LiveMotionHost, stop: LiveStop): LiveMotion {
  const now = host.now ?? (() => performance.now())
  const frame = host.frame ?? ((cb) => requestAnimationFrame(cb))
  const cancelFrame = host.cancelFrame ?? ((id) => cancelAnimationFrame(id))
  const from = host.view()
  const tr: FilmTransition = resolveTransition(stop.transition, from, stop.view, from === null)
  const effects = stop.effects && Object.keys(stop.effects).length ? stop.effects : null
  const paintHold = stop.open ? 1e9 : stop.hold
  let id = 0
  let cancelled = false
  let arrive!: () => void
  let finish!: () => void
  const arrived = new Promise<void>((r) => { arrive = r })
  const done = new Promise<void>((r) => { finish = r })

  if (host.reducedMotion()) {
    host.jumpTo(stop.view)
    arrive()
    if (effects) host.paint?.(effects, { local: 1, hold: paintHold })
    finish()
    return { cancel: () => { cancelled = true }, arrived, done }
  }

  const start = now()
  let arrivedYet = false
  const tick = () => {
    if (cancelled) return
    const t = (now() - start) / 1000
    if (from && t < tr.seconds) {
      host.jumpTo(arrivalView(from, stop.view, tr, t / tr.seconds))
      id = frame(tick)
      return
    }
    const local = t - (from ? tr.seconds : 0)
    const p = stop.hold > 0 ? Math.min(1, local / stop.hold) : 1
    // Land exactly on the stop when the arrival ends; then only an orbit or a push moves the camera.
    if (tr.during !== 'still') host.jumpTo(holdView(stop.view, tr, p))
    else if (!arrivedYet) host.jumpTo(stop.view)
    if (!arrivedYet) { arrivedYet = true; arrive() }
    if (effects) host.paint?.(effects, { local, hold: paintHold })
    if (p >= 1) {
      // A step that waits for Next keeps its effects, repainted as the globe moves under them, until it is left.
      if (effects && stop.open) { finish(); id = frame(tick); return }
      if (effects) host.clear?.()
      finish()
      return
    }
    id = frame(tick)
  }
  id = frame(tick)
  return {
    cancel() {
      cancelled = true
      cancelFrame(id)
      host.clear?.()
      arrive()
      finish()
    },
    arrived,
    done,
  }
}

/** A film stop as a live stop. */
export const liveStopOf = (stop: FilmStop, open = false): LiveStop => ({
  view: stop.view, transition: stop.transition, hold: stop.hold, effects: stop.effects, open,
})
